import API from 'services/API';
import { useTranslation, Trans } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useAppMutation from 'hooks/useAppMutation';
import useNotify from 'hooks/useNotify';
import useAppModal from 'hooks/useAppModal';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { ICALS_BASE_QUERY_KEY } from './useFetchIcals';

const useMutationsIcals = () => {
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { openConfirmModal } = useAppModal();
  const { t } = useTranslation();

  const { mutateAsync: removeIcalMutation } = useAppMutation({
    mutationFn: (uid: string) => API.delete(`/api/internal/icals/${uid}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ICALS_BASE_QUERY_KEY],
      });
      notifySuccess(
        t(
          'pageAutomations.calendarTools.calendarImportSection.deleteModal.success',
        ),
      );
    },
    onError: (error: any) => {
      notifyError(
        getErrorMessage({
          apiErrorCode: error?.response?.data?.apiErrorCode,
          apiErrorMessage: error?.response?.data?.apiErrorMessage,
          baseKey:
            'pageAutomations.calendarTools.calendarImportSection.apiErrorCodes.',
          t,
        }),
      );
    },
  });

  const { mutate: synchronizeIcal } = useAppMutation({
    mutationFn: (uid: string) =>
      API.post(`/api/internal/icals/${uid}/synchronize`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ICALS_BASE_QUERY_KEY],
      });
      notifySuccess(
        t(
          'pageAutomations.calendarTools.calendarImportSection.synchronizationSuccess',
        ),
      );
    },
    onError: (error: any) => {
      notifyError(
        getErrorMessage({
          apiErrorCode: error?.response?.data?.apiErrorCode,
          apiErrorMessage: error?.response?.data?.apiErrorMessage,
          baseKey:
            'pageAutomations.calendarTools.calendarImportSection.apiErrorCodes.',
          t,
        }),
      );
    },
  });

  const handleRemoveIcal = async (uid: string) => {
    await removeIcalMutation(uid);
  };

  const removeIcal = (uid: string) => {
    openConfirmModal({
      title: t(
        'pageAutomations.calendarTools.calendarImportSection.deleteModal.title',
      ),
      body: (
        <Trans
          i18nKey="pageAutomations.calendarTools.calendarImportSection.deleteModal.description"
          components={{ br: <br /> }}
        />
      ),
      onConfirmAsync: () => handleRemoveIcal(uid),
      confirmColor: 'danger',
      confirmLabel: t(
        'pageAutomations.calendarTools.calendarImportSection.deleteModal.removeIcalLink',
      ),
    });
  };

  return { removeIcal, synchronizeIcal };
};

export default useMutationsIcals;
